/**
 * The undo stack.
 *
 * A bounded stack of recent edit-mode actions, each carrying a closure that reverses itself.
 * WHAT IT DOES NOT DO is model the edits: `delNode` knows how to put a node back and
 * `undoDrag` knows that a drop changes colour, depth and side across a whole subtree, so each
 * pushes a closure that already contains everything needed. This module holds them, bounds
 * them, and hands the top one back — nothing here understands the map.
 *
 * CLEARED ON EVERY FRESH LOAD (page load, language switch, reload-from-server), because an
 * entry pushed before a reload closes over pre-reload state and would restore a node that no
 * longer exists into a map that has moved on.
 *
 * `onChange` is how the Undo button learns. "Can I undo" and "undo WHAT" are the same
 * question for every consumer, which is why `label()` returns the top entry's label or null
 * rather than exposing the stack — nothing should be reading past the top of it.
 */

export interface UndoEntry {
  label: string;
  undo: () => Promise<void> | void;
}

const LIMIT = 20;

let stack: UndoEntry[] = [];
let onChange: () => void = () => {};

/** Told once, at boot, how to say the stack moved. */
export function setUndoListener(fn: () => void): void {
  onChange = fn;
}

export function pushUndo(label: string, undo: UndoEntry['undo']): void {
  stack.push({ label, undo });
  if (stack.length > LIMIT) stack.shift();
  onChange();
}

/** What the next undo would undo, or null when there is nothing. */
export function undoLabel(): string | null {
  return stack.length ? stack[stack.length - 1].label : null;
}

export function clearUndo(): void {
  stack = [];
  onChange();
}

/**
 * Run the top entry.
 *
 * The entry is popped BEFORE it runs and is not put back if it throws. That is deliberate:
 * an undo that failed halfway has already changed some of what it was reversing, so offering
 * to run it again would compound the damage rather than repair it. Say so and move on.
 */
export async function performUndo(): Promise<void> {
  const entry = stack.pop();
  onChange();
  if (!entry) return;
  try {
    await entry.undo();
  } catch (e) {
    console.error('Undo failed:', e);
    alert(`Could not undo "${entry.label}".`);
  }
}
